import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useExplorerStore } from "../../store/explorerStore";
import { useFolderTree } from "../../hooks/useFolderTree";

// ─── tiny helpers ────────────────────────────────────────────────────────────

const ChevronIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0 text-gray-600"
    viewBox="0 0 16 16"
    fill="currentColor"
  >
    <path d="M6.22 4.22a.75.75 0 011.06 0l3.25 3.25a.75.75 0 010 1.06l-3.25 3.25a.75.75 0 01-1.06-1.06L9.19 8 6.22 5.03a.75.75 0 010-1.06z" />
  </svg>
);

const FolderIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0 text-yellow-400"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
  </svg>
);

// ─── layout calculation ───────────────────────────────────────────────────────
// Given measured pixel widths, figure out how many "tail" items (just before
// the last/current item) can be shown between the "…" and the current folder.
//
// Visible layout:
//   My Files  >  [first]  >  …  >  [tail N-1]  >  …  >  [tail 0]  >  [current]
//
// We always show: root, first folder, current folder.
// We fill tail slots from right-to-left (most-recent ancestor first).
// Everything that doesn't fit goes into the "…" dropdown.

interface Widths {
  root: number;       // "My Files"
  segments: number[]; // each segment = chevron + folder name, index matches breadcrumb
  ellipsis: number;   // chevron + "…" button
}

function calcVisibleTailCount(
  containerWidth: number,
  w: Widths,
): number | "all" {
  if (!w.root || w.segments.length === 0) return "all";

  const totalNeeded = w.root + w.segments.reduce((s, x) => s + x, 0);
  if (totalNeeded <= containerWidth) return "all";

  // With only 1 folder there is nothing to collapse
  if (w.segments.length <= 1) return "all";

  // Fixed cost: root + first segment + ellipsis segment + last segment
  const fixed =
    w.root +
    (w.segments[0] ?? 0) +
    w.ellipsis +
    (w.segments[w.segments.length - 1] ?? 0);

  let remaining = containerWidth - fixed;
  let tailCount = 0;

  // Try to fit items from right-to-left (skip index 0 = first, skip last = current)
  for (let i = w.segments.length - 2; i >= 1; i--) {
    const itemW = w.segments[i] ?? 0;
    if (remaining >= itemW) {
      remaining -= itemW;
      tailCount++;
    } else {
      break;
    }
  }

  return tailCount;
}

// ─── component ───────────────────────────────────────────────────────────────

export function Breadcrumb() {
  const currentFolderId = useExplorerStore((s) => s.currentFolderId);
  const setCurrentFolder = useExplorerStore((s) => s.setCurrentFolder);
  const breadcrumb = useFolderTree(currentFolderId);

  const navRef = useRef<HTMLElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(9999);
  const [widths, setWidths] = useState<Widths>({ root: 0, segments: [], ellipsis: 0 });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ellipsisRef = useRef<HTMLButtonElement>(null);

  const breadcrumbKey = breadcrumb.map((b) => b.id).join(",");

  // ── Track container width ──────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (navRef.current) setContainerWidth(navRef.current.clientWidth);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0]!.contentRect.width);
    });
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  // ── Measure ghost div after every breadcrumb or container-width change ─────
  useLayoutEffect(() => {
    const ghost = ghostRef.current;
    if (!ghost) return;
    const children = Array.from(ghost.children) as HTMLElement[];
    // ghost children order: root, seg0, seg1, …, segN, ellipsisSegment
    const root = children[0]?.offsetWidth ?? 0;
    const ellipsis = children[children.length - 1]?.offsetWidth ?? 0;
    const segments = children.slice(1, -1).map((el) => el.offsetWidth);
    setWidths({ root, segments, ellipsis });
  }, [breadcrumbKey, containerWidth]);

  // Close dropdown on breadcrumb navigate
  useEffect(() => {
    setDropdownOpen(false);
  }, [breadcrumbKey]);

  // ── Compute layout ─────────────────────────────────────────────────────────
  const visibleTailCount = useMemo(
    () => calcVisibleTailCount(containerWidth, widths),
    [containerWidth, widths],
  );

  // ── Partition ──────────────────────────────────────────────────────────────
  const firstItem = breadcrumb[0] ?? null;
  const lastItem  = breadcrumb[breadcrumb.length - 1] ?? null;
  // items between first and last (exclusive)
  const middle = breadcrumb.length >= 3 ? breadcrumb.slice(1, -1) : [];

  let hiddenItems: typeof breadcrumb = [];
  let visibleMiddle: typeof breadcrumb = [];

  if (visibleTailCount !== "all" && middle.length > 0) {
    // tail items are the LAST `visibleTailCount` of middle (most-recent ancestors)
    visibleMiddle = middle.slice(middle.length - visibleTailCount);
    hiddenItems   = middle.slice(0, middle.length - visibleTailCount);
  } else {
    visibleMiddle = middle;
  }

  const showEllipsis = hiddenItems.length > 0;

  const openDropdown = () => {
    const btn = ellipsisRef.current;
    if (btn) {
      const r = btn.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 6, left: r.left });
    }
    setDropdownOpen((v) => !v);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/*
        Ghost div — off-screen, identical styles, used only for width measurement.
        One child per segment + one extra for the ellipsis button segment.
      */}
      <div
        aria-hidden="true"
        ref={ghostRef}
        className="pointer-events-none fixed left-0 top-[-9999px] flex items-center gap-0 whitespace-nowrap text-sm opacity-0"
      >
        {/* root */}
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <FolderIcon />
          My Files
        </span>
        {/* one span per breadcrumb folder (chevron + folder icon + name) */}
        {breadcrumb.map((item) => (
          <span key={item.id} className="flex items-center gap-1.5">
            <ChevronIcon />
            <FolderIcon />
            <span className="whitespace-nowrap">{item.name}</span>
          </span>
        ))}
        {/* ellipsis segment */}
        <span className="flex items-center">
          <ChevronIcon />
          <span className="px-1.5">…</span>
        </span>
      </div>

      {/* ── Real nav ────────────────────────────────────────────────────────── */}
      <nav
        ref={navRef}
        className="flex min-w-0 items-center overflow-hidden text-sm"
        aria-label="Breadcrumb"
      >
        {/* Root */}
        <Link
          to="/app"
          className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-gray-400 transition-colors hover:text-gray-200"
          onClick={() => setCurrentFolder(null)}
        >
          <FolderIcon />
          My Files
        </Link>

        {/* ── Nothing deep → we're already at root ── */}
        {breadcrumb.length === 0 && null}

        {/* ── One folder deep → just show it ── */}
        {breadcrumb.length === 1 && lastItem && (
          <span className="flex min-w-0 items-center gap-1.5">
            <ChevronIcon />
            <FolderIcon />
            <span
              className="truncate whitespace-nowrap font-medium text-gray-200"
              title={lastItem.name}
            >
              {lastItem.name}
            </span>
          </span>
        )}

        {/* ── Two or more folders deep ── */}
        {breadcrumb.length >= 2 && firstItem && lastItem && (
          <>
            {/* First folder — always visible */}
            <span className="flex flex-shrink-0 items-center gap-1.5">
              <ChevronIcon />
              <FolderIcon />
              <Link
                to={`/app/folder/${firstItem.id}`}
                className="max-w-[90px] truncate whitespace-nowrap text-gray-400 transition-colors hover:text-gray-200 sm:max-w-[140px]"
                title={firstItem.name}
                onClick={() => setCurrentFolder(firstItem.id)}
              >
                {firstItem.name}
              </Link>
            </span>

            {/* Ellipsis — only when some middle items are hidden */}
            {showEllipsis && (
              <span className="flex flex-shrink-0 items-center">
                <ChevronIcon />
                <button
                  ref={ellipsisRef}
                  onClick={openDropdown}
                  className="rounded px-1.5 py-0.5 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
                  title="Show hidden folders"
                >
                  &hellip;
                </button>
              </span>
            )}

            {/* Visible middle items (most-recent ancestors before current) */}
            {visibleMiddle.map((item) => (
              <span key={item.id} className="flex flex-shrink-0 items-center gap-1.5">
                <ChevronIcon />
                <FolderIcon />
                <Link
                  to={`/app/folder/${item.id}`}
                  className="max-w-[90px] truncate whitespace-nowrap text-gray-400 transition-colors hover:text-gray-200 sm:max-w-[140px]"
                  title={item.name}
                  onClick={() => setCurrentFolder(item.id)}
                >
                  {item.name}
                </Link>
              </span>
            ))}

            {/* Current folder — always last, can shrink/truncate if very long name */}
            <span className="flex min-w-0 items-center gap-1.5">
              <ChevronIcon />
              <FolderIcon />
              <span
                className="truncate whitespace-nowrap font-medium text-gray-200"
                title={lastItem.name}
              >
                {lastItem.name}
              </span>
            </span>
          </>
        )}
      </nav>

      {/* ── Dropdown (portaled so overflow:hidden on nav doesn't clip it) ───── */}
      {dropdownOpen &&
        createPortal(
          <>
            {/* click-away */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setDropdownOpen(false)}
            />
            <div
              className="fixed z-50 min-w-[180px] rounded-lg border border-gray-700 bg-gray-900 py-1 shadow-xl"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
            >
              {hiddenItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/app/folder/${item.id}`}
                  onClick={() => {
                    setCurrentFolder(item.id);
                    setDropdownOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                >
                  <FolderIcon />
                  <span className="max-w-[240px] truncate">{item.name}</span>
                </Link>
              ))}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
